import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import {
  ClientMaterial,
  DuplicateExisting,
  MATERIAL_CATEGORY_LABEL,
  MaterialCategory,
  updateClientMaterialMeta,
} from "@/api/clientMaterials";
import { ApiClientError } from "@/api/client";

/**
 * D-146 W2 — «Змінити назву / категорію» (meta-only; the binary and the
 * original filename are immutable — a new file is always a NEW material).
 * Duplicate 409 → explicit rename choice: «Залишити іншу назву» /
 * «Все одно зберегти» (confirmDuplicate).
 */
export function MaterialEditModal({
  material,
  onClose,
  onSaved,
}: {
  material: ClientMaterial;
  onClose: () => void;
  onSaved: (item: ClientMaterial) => void;
}) {
  const [name, setName] = useState(material.name);
  const [category, setCategory] = useState<MaterialCategory>(material.category);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateExisting | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const valid = name.trim().length > 0 && name.trim().length <= 255;

  const save = (confirmDuplicate: boolean) => {
    if (!valid) return;
    setBusy(true);
    setError(null);
    updateClientMaterialMeta(material.id, {
      name: name.trim(),
      category,
      confirmDuplicate,
    })
      .then((r) => onSaved(r.item))
      .catch((e: unknown) => {
        if (e instanceof ApiClientError && e.code === "duplicate_material") {
          const existing = (e.details as { existing?: DuplicateExisting } | undefined)?.existing;
          setDuplicate(existing ?? { id: "", name: "", originalFilename: "", createdAt: "", createdBy: "" });
          return;
        }
        setError(e instanceof Error ? e.message : "Не вдалося зберегти.");
      })
      .finally(() => setBusy(false));
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 sm:items-center sm:p-6"
      onClick={() => !busy && onClose()}
      aria-hidden
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Змінити назву або категорію"
        onClick={(e) => e.stopPropagation()}
        className="w-full rounded-t-2xl bg-background p-5 shadow-2xl sm:max-w-md sm:rounded-2xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">Змінити назву / категорію</h2>
          <button
            type="button"
            aria-label="Закрити"
            disabled={busy}
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mb-3 text-xs text-muted-foreground">
          Оригінальний файл: <span className="font-medium">{material.originalFilename}</span> (незмінний)
        </p>

        <div className="mb-3">
          <label className="mb-1 block text-xs font-semibold" htmlFor="edit-material-name">
            Назва матеріалу <span className="text-destructive">*</span>
          </label>
          <input
            id="edit-material-name"
            value={name}
            maxLength={255}
            disabled={busy}
            onChange={(e) => {
              setName(e.target.value);
              setDuplicate(null);
            }}
            className="w-full rounded-lg border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        <div className="mb-4">
          <label className="mb-1 block text-xs font-semibold" htmlFor="edit-material-category">
            Категорія <span className="text-destructive">*</span>
          </label>
          <select
            id="edit-material-category"
            value={category}
            disabled={busy}
            onChange={(e) => setCategory(e.target.value as MaterialCategory)}
            className="w-full rounded-lg border bg-card px-3 py-2 text-sm"
          >
            {(Object.keys(MATERIAL_CATEGORY_LABEL) as MaterialCategory[]).map((c) => (
              <option key={c} value={c}>{MATERIAL_CATEGORY_LABEL[c]}</option>
            ))}
          </select>
        </div>

        {error && (
          <div role="alert" className="mb-3 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        {duplicate && (
          <div className="mb-3 rounded-lg border border-warning-foreground/25 bg-warning px-3 py-2.5 text-sm text-warning-foreground">
            <p className="font-medium">Матеріал зі схожою назвою вже є в цьому холдингу{duplicate.name ? `: «${duplicate.name}»` : ""}.</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setDuplicate(null)}
                className="rounded-md border border-warning-foreground/30 px-2.5 py-1.5 text-xs font-semibold"
              >
                Залишити іншу назву
              </button>
              <button
                type="button"
                onClick={() => save(true)}
                className="rounded-md bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground"
              >
                Все одно зберегти
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded-lg border px-3.5 py-2 text-sm font-medium hover:bg-accent disabled:opacity-40"
          >
            Скасувати
          </button>
          <button
            type="button"
            disabled={!valid || busy || duplicate !== null}
            onClick={() => save(false)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Зберегти
          </button>
        </div>
      </div>
    </div>
  );
}
